import type { AgreementBillingPeriod, AgreementTerms } from '@/types/agreement-terms';
import { normalizeAgreementTermMonths } from '@/lib/constants/agreement-term-months';
import type { Deal, RecurringType } from '@/types/deals';
import type { InvoiceLineItem } from '@/types/invoices';
import type { Location } from '@/types/location';
import type { QuoteItem } from '@/types/quotes';
import type {
  SlaAgreementDraftPayload,
  SlaAgreementHardwareRow,
  SlaAgreementReconciliation,
  SlaAgreementSourceType,
} from '@/types/sla-agreements';

function isSoftwareOrShippingLine(description: string): boolean {
  const normalized = description.trim().toLowerCase();
  return normalized.includes('software license fee') || normalized.includes('shipping');
}

export function normalizeAgreementHardwareItemName(description: string | null | undefined): string {
  if (!description) {
    return '';
  }

  const [firstLine = ''] = description.split(/\r?\n/, 1);
  const strippedIncludes = firstLine.replace(/\s+includes\s*:.*$/i, '');
  return strippedIncludes.replace(/\s+/g, ' ').trim();
}

export interface AgreementContactCandidate {
  id?: string;
  can_select?: boolean;
  access_state?: 'editable_legacy' | 'protected_canonical';
  name: string | null;
  email: string | null;
  role: string | null;
  is_primary?: boolean | null;
}

type HardwareSourceItem = Pick<
  QuoteItem | InvoiceLineItem,
  'line_number' | 'description' | 'quantity' | 'unit_price' | 'line_total' | 'display_type_id'
> & {
  id?: string;
};

export interface BuildSlaAgreementDraftPayloadInput {
  source_type: SlaAgreementSourceType;
  source_reference: string | null;
  source_quote_number?: string | null;
  customer_name: string | null;
  agreement_terms?: AgreementTerms | null;
  deal?: Deal | null;
  location?: Location | null;
  items?: HardwareSourceItem[];
  source_contact?: AgreementContactCandidate | null;
  deal_contact?: AgreementContactCandidate | null;
  location_contacts?: AgreementContactCandidate[];
}

export function recurringTypeToAgreementBillingPeriod(
  recurringType: RecurringType | null | undefined
): AgreementBillingPeriod | null {
  if (recurringType === 'mrr') return 'monthly';
  if (recurringType === 'qrr') return 'quarterly';
  if (recurringType === 'arr') return 'annually';
  return null;
}

function isRenderableHardwareLine(item: HardwareSourceItem): boolean {
  return (
    item.quantity > 0 &&
    !!item.description?.trim() &&
    !isSoftwareOrShippingLine(item.description)
  );
}

export function isRenderableAgreementHardwareRow(row: Pick<SlaAgreementHardwareRow, 'description' | 'quantity'>): boolean {
  return row.quantity > 0 && Boolean(row.description?.trim());
}

export function filterRenderableAgreementHardwareRows(
  rows: SlaAgreementHardwareRow[]
): SlaAgreementHardwareRow[] {
  return rows.filter(isRenderableAgreementHardwareRow);
}

export function buildHardwareScheduleRows(items: HardwareSourceItem[]): SlaAgreementHardwareRow[] {
  return items
    .filter(isRenderableHardwareLine)
    .map((item, index) => ({
      id: item.id || `hardware-${index + 1}`,
      line_number: item.line_number,
      description: normalizeAgreementHardwareItemName(item.description),
      quantity: item.quantity,
      unit_price: item.unit_price,
      line_total: item.line_total,
      display_type_id: item.display_type_id,
    }));
}

export function fallbackAgreementTerms(input: {
  agreementTerms?: AgreementTerms | null;
  deal?: Deal | null;
  location?: Location | null;
}): AgreementTerms | null {
  const source = input.agreementTerms;
  const fromDeal: AgreementTerms | null = input.deal
    ? {
        pos_system: input.deal.pos_system,
        software_license_fee_amount: input.deal.recurring_amount,
        software_license_fee_period: recurringTypeToAgreementBillingPeriod(input.deal.recurring_type),
      }
    : null;

  if (fromDeal?.pos_system || fromDeal?.software_license_fee_amount || fromDeal?.software_license_fee_period) {
    return {
      pos_system: fromDeal.pos_system,
      software_license_fee_amount: source?.software_license_fee_amount ?? fromDeal.software_license_fee_amount,
      software_license_fee_period: fromDeal.software_license_fee_period ?? source?.software_license_fee_period ?? null,
    };
  }

  if (source?.pos_system || source?.software_license_fee_amount || source?.software_license_fee_period) {
    return source;
  }

  if (!input.location) {
    return null;
  }

  const fromLocation: AgreementTerms = {
    pos_system: input.location.pos_system,
    software_license_fee_amount: input.location.monthly_license_fee,
    software_license_fee_period: input.location.license_fee_period,
  };

  if (
    fromLocation.pos_system === null &&
    fromLocation.software_license_fee_amount === null &&
    fromLocation.software_license_fee_period === null
  ) {
    return null;
  }

  return fromLocation;
}

function getPrimaryLocationContact(
  contacts: AgreementContactCandidate[] | null | undefined
): AgreementContactCandidate | null {
  if (!contacts?.length) {
    return null;
  }

  const allowed = contacts.filter(contact => contact.can_select !== false && contact.access_state !== 'protected_canonical');
  return allowed.find(contact => contact.is_primary) || allowed[0] || null;
}

function resolveSignerPrefill(input: {
  sourceContact?: AgreementContactCandidate | null;
  dealContact?: AgreementContactCandidate | null;
  deal?: Deal | null;
  location?: Location | null;
  locationContacts?: AgreementContactCandidate[];
}): {
  signer_name: string | null;
  signer_title: string | null;
  signer_email: string | null;
  notice_address: string | null;
} {
  const explicit = input.sourceContact || input.dealContact;
  if (explicit && (explicit.can_select === false || explicit.access_state === 'protected_canonical')) {
    return {signer_name:null,signer_title:null,signer_email:null,notice_address:input.location?.address || input.deal?.address || null};
  }
  const primaryLocationContact = getPrimaryLocationContact(input.locationContacts);
  const contactCandidate =
    input.sourceContact ||
    input.dealContact ||
    (input.deal?.prospect_contact_name || input.deal?.prospect_contact_email
      ? {
          name: input.deal.prospect_contact_name,
          email: input.deal.prospect_contact_email,
          role: input.deal.prospect_contact_role,
        }
      : null) ||
    primaryLocationContact ||
    null;

  return {
    signer_name: contactCandidate?.name?.trim() || input.location?.contract_signee_name || null,
    signer_title: contactCandidate?.role?.trim() || input.location?.contract_signee_title || null,
    signer_email: contactCandidate?.email?.trim() || null,
    notice_address: input.location?.address || input.deal?.address || null,
  };
}

export function buildSlaAgreementDraftPayload(input: BuildSlaAgreementDraftPayloadInput): SlaAgreementDraftPayload {
  const {
    source_type,
    source_reference,
    source_quote_number,
    customer_name,
    agreement_terms,
    deal,
    location,
    items = [],
    source_contact,
    deal_contact,
    location_contacts = [],
  } = input;

  const terms = fallbackAgreementTerms({
    agreementTerms: agreement_terms,
    deal,
    location,
  });
  const signer = resolveSignerPrefill({
    sourceContact: source_contact,
    dealContact: deal_contact,
    deal,
    location,
    locationContacts: location_contacts,
  });

  return {
    source_type,
    source_reference,
    source_quote_number: source_quote_number || null,
    customer_name,
    legal_entity_name: null,
    primary_location_name: location?.location_name || null,
    primary_location_address: location?.address || null,
    signer_name: signer.signer_name,
    signer_title: signer.signer_title,
    signer_email: signer.signer_email,
    notice_address: signer.notice_address,
    pos_system: terms?.pos_system || null,
    contract_term_months: normalizeAgreementTermMonths(null),
    software_license_fee_amount: terms?.software_license_fee_amount ?? null,
    software_license_fee_period: terms?.software_license_fee_period ?? null,
    stores: [
      {
        id: 'store-1',
        expected_location_id: location?.id || null,
        name: location?.location_name || customer_name || null,
        address: location?.address || deal?.address || null,
        tentative_installation_date: null,
      },
    ],
    hardware_schedule_rows: buildHardwareScheduleRows(items),
  };
}

export function getBillingOutputs(input: {
  amount: number | null;
  period: AgreementBillingPeriod | null;
}): Record<AgreementBillingPeriod, string> {
  const { amount, period } = input;

  const fallback = {
    monthly: 'N/A',
    quarterly: 'N/A',
    annually: 'N/A',
  } satisfies Record<AgreementBillingPeriod, string>;

  if (amount === null || period === null) {
    return fallback;
  }

  return {
    monthly: period === 'monthly' ? formatAgreementCurrency(amount) : 'N/A',
    quarterly: period === 'quarterly' ? formatAgreementCurrency(amount) : 'N/A',
    annually: period === 'annually' ? formatAgreementCurrency(amount) : 'N/A',
  };
}

export function formatAgreementCurrency(amount: number | null): string {
  if (amount === null || Number.isNaN(amount)) {
    return '';
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
}

export function getRenderedCustomerValue<T extends string | null | undefined>(
  value: T,
  fallback: string = '____________________'
): string {
  const normalized = typeof value === 'string' ? value.trim() : value;
  return normalized ? String(normalized) : fallback;
}

export function getReconciledStoreCount(
  draft: SlaAgreementDraftPayload,
  reconciliation: SlaAgreementReconciliation | null
): number {
  return reconciliation?.stores.length || draft.stores.length || 1;
}
